import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { ClientService } from '/src/api';
import { Box, BreadcrumbNav, FacilityList, InfoBox, ListPage, PageHeader, Text } from '/src/components';
import { config } from '/src/config';
import { useGlobalStore, useUserStore } from '/src/context';
import { useProductTour } from '/src/hooks/useProductTour';
import { Client } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { getPageTitle } from '/src/utils';
import { facilitiesListTour } from '/src/utils/productTours';

export const ListFacilitiesPage = observer(() => {
  /** Context **/
  const userStore = useUserStore();
  const globalStore = useGlobalStore();
  const params = useParams();
  const { t } = useTranslation();

  /** State **/
  const [clientState, setClientState] = useState<{ id: number; client: Client | null }>();

  /** Computed **/
  const clientId = parseInt(params.client_id ?? '');
  const client = clientState?.id === clientId ? clientState.client : null;
  const isLoading = !client;

  const titleBar = useMemo(() => {
    const badges: ReactNode[] = [];

    if (client?.is_demo) {
      badges.push(
        <InfoBox
          key="demo"
          pad={{ horizontal: '0.5rem' }}
          gap="xsmall"
          background={{ color: 'status-critical', opacity: 'medium' }}
          border={{ color: 'status-critical' }}
        >
          <Text size="0.95rem" weight={600} toUpperCase>
            Demo Client
          </Text>
        </InfoBox>
      );
    }

    return (
      <Box gap="small" direction="row">
        {badges}
      </Box>
    );
  }, [client?.is_demo]);

  /** Effects **/
  useEffect(() => {
    if (!userStore.user) return;

    if (userStore.isExternalUser) {
      setClientState({ id: clientId, client: userStore.getUserClient(clientId) ?? null });
      return;
    }

    let isCurrent = true;
    ClientService.get({ id: clientId })
      .then((client) => {
        if (isCurrent) setClientState({ id: clientId, client });
      })
      .catch((err) => {
        if (isCurrent) globalStore.handleApiError(err, toastMessages.listFacilities.error);
      });

    return () => {
      isCurrent = false;
    };
  }, [clientId, userStore.user, userStore.isExternalUser, userStore.getUserClient, globalStore]);

  useEffect(() => {
    if (client) {
      document.title = getPageTitle(config.client.basePageTitle, 'Facilities', client.name);
    }
  }, [client?.name]);

  /* Product Tour */
  useProductTour({
    tourName: 'facilities-list-tour',
    steps: facilitiesListTour,
    autoStart: true,
  });

  /** Render **/
  return (
    <ListPage
      title={client?.name || ''}
      pageHeader={
        <PageHeader
          title={client?.name || ''}
          titleBar={titleBar}
          showAddButton={userStore.isAdminUser && !userStore.isFacilityUser}
          addButtonLabel={t('facility:addFacility')}
          addButtonUrl={`/clients/${clientId}/facilities/create`}
          dataTour="add-facility-button"
        />
      }
      breadcrumbNav={
        <BreadcrumbNav
          previousPages={[
            { name: 'Clients', link: !userStore.isExternalUser ? '/clients' : undefined },
            { name: client?.name || '', link: !userStore.isFacilityUser ? `/clients/${clientId}` : undefined },
          ]}
          currentPageName="Facilities"
        />
      }
      isLoading={isLoading}
    >
      <FacilityList clientId={clientId} showSearchInput standalone data-tour="facilities-list" />
    </ListPage>
  );
});
